import { animate, query, stagger, style, transition, trigger } from '@angular/animations';
import { Component, OnInit, OnDestroy, ChangeDetectorRef, ChangeDetectionStrategy } from '@angular/core';
import { Location } from "@angular/common";
import { MainnavService, ViewType } from '../mainNav/mainnav.service';
import { BottomSheetHelperService } from '../../services/bottomSheetHelper.service';
import { ConfirmationModalDialogComponent, SecureCheckDialogComponent } from '../../modalDialogs';
import { ExternalLinksSettingsService } from '../../services/ExternalLinksSettings.service';
import { ServiceCallService } from './serviceCall.service';
import { OverlaySpinnerService } from '../overlayspinner/overlaySpiner.service';
import { Subscription } from 'rxjs';
import { ServiceCallDataModel } from '../../shared/models';
import { CoreLogService } from '../../services/log.service';
import Utils from '../../shared/utils';
import { InHouseOrderService } from '../../services/inHouseOrder.service';
import { req_ServiceCall, ServiceCallStatus } from '../../services/IInHouseOrderService';

/** ??? */
@Component({
  selector: 'app-serviceCall',
  templateUrl: './serviceCall.component.html',
  styleUrls: ['./serviceCall.component.scss'],
  providers: [ServiceCallService, OverlaySpinnerService],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger("serviceButtonEntering", [
      transition("false => true", [
        query(".animate", [
          style({ transform: "scale(0)" }),
          stagger(100, [
            animate("400ms ease-out", style({ transform: "scale(1.1)" })),
            animate("100ms ease-in-out", style({ transform: "scale(1)" }))
          ])
        ])
      ])
    ]),
  ]
})
export class ServiceCallComponent implements OnInit, OnDestroy {
  public isSmallView: boolean = false;

  public isLoaded: boolean = false;

  private _serviceCallsSubscriber: Subscription;

  constructor(
    private _serviceCallService: ServiceCallService,
    private _changeDetectorRef: ChangeDetectorRef,
    private _coreLogService: CoreLogService,
    private _location: Location,
    private _bottomSheetHelperService: BottomSheetHelperService,
    private _externalLinksSettingsService: ExternalLinksSettingsService,
    private _inHouseOrderService: InHouseOrderService,
    private _overlaySpinnerService: OverlaySpinnerService,
    mainnavService: MainnavService,
  ) {
    this.isSmallView = mainnavService.viewType === ViewType.Small;
  }

  ngOnInit() {
    const me = this;

    me._serviceCallsSubscriber = me._serviceCallService.ObserveServiceCallsChanges.subscribe(async () => {
      try {
        if (await me.bindServiceCalls())
          me.PushChanges();
      } catch (error) {
        me._coreLogService.error("ServiceCallComponent.ngOnInit", "ObserveServiceCallsChanges.bindServiceCalls.catch", error);
      }
    });

    me.bindServiceCalls().then(() => {
      me.isLoaded = true;
      me.PushChanges();
    }).catch((error) => {
      me._coreLogService.error("ServiceCallComponent.ngOnInit", "bindServiceCalls.catch", error);
      me.PushChanges();
    })
  }

  ngOnDestroy() {
    const me = this;
    if (me._serviceCallsSubscriber)
      me._serviceCallsSubscriber.unsubscribe();
  }

  private PushChanges(): void {
    try {
      this._changeDetectorRef.detectChanges();
    } catch (error) {
      this._coreLogService.error("ServiceCallComponent.PushChanges", "_changeDetectorRef.detectChanges (catch)", error);
    }
  }

  public ServiceCalls: ServiceCallDataModel[] = [];
  /** ermittelt die aktuellen ServiceCalls */
  private async bindServiceCalls(): Promise<boolean> {
    const me = this;
    try {
      me.ServiceCalls = await me._serviceCallService.GetServiceCalls();
      return true;
    } catch (error) {
      me._coreLogService.error("ServiceCallComponent.bindServiceCalls", "catch", error);
      return false;
    }
  }

  /**
   * 1. Open Dialog for opening an extern Link
   * 2. Open Dialog for a servicecall
   */
  public OpenDialog(serviceCall: ServiceCallDataModel) {
    const me = this;
    if (!Utils.IsNullOrWhiteSpace(serviceCall.url)) {
      me._externalLinksSettingsService.RouteToExternalLinks(serviceCall.url, true);
    } else {
      me._overlaySpinnerService.ShowLayoutSpinner();
      const bottomSheetRef = me._bottomSheetHelperService.open(SecureCheckDialogComponent, {
        data: {
          title: "phServiceCall_SecureCheckDialog_Title",
          description: serviceCall.description_de,
          buttonTextAccept: "phServiceCall_SecureCheckDialog_AcceptBtn_Label",
          buttonTextDeny: "phServiceCall_SecureCheckDialog_DenyBtn_Label",
          icon: "shop-icons.svg#ya-hypersoft-achtung"
        },
        panelClass: "mat-bottom-sheet-container-medium"
      });
      bottomSheetRef.afterDismissed().subscribe((result: boolean) => {
        if (result) {
          const request = new req_ServiceCall();
          request.message = serviceCall.pos_message;
          if (Utils.IsNullOrWhiteSpace(request.message))
            request.message = serviceCall.label_de;
          me._inHouseOrderService.ServiceCall(request).then((response) => {
            if (response.status == ServiceCallStatus.Okay) {
              me._overlaySpinnerService.HideLayoutSpinner();
              me._bottomSheetHelperService.open(ConfirmationModalDialogComponent, {
                data: {
                  title: "phServiceCall_Success_Title",
                  buttonText: "phServiceCall_Button",
                  icon: true
                },
                panelClass: "mat-bottom-sheet-container-medium"
              });
            } else {
              me._overlaySpinnerService.HideLayoutSpinner();
              me._bottomSheetHelperService.open(SecureCheckDialogComponent, {
                data: {
                  title: "phServiceCall_Fail_Title",
                  buttonTextAccept: "phServiceCall_Button",
                  icon: "shop-icons.svg#ya-hypersoft-achtung"
                },
              });
              me._coreLogService.error("ServiceCallComponent.OpenDialog", "status not okay", response);
            }
          }).catch((error) => {
            me._overlaySpinnerService.HideLayoutSpinner();
            me._bottomSheetHelperService.open(SecureCheckDialogComponent, {
              data: {
                title: "phServiceCall_Fail_Title",
                buttonTextAccept: "phServiceCall_Button",
                icon: "shop-icons.svg#ya-hypersoft-achtung"
              },
            });
            me._coreLogService.error("ServiceCallComponent.bindServiceCalls", "catch", error);
          });
        } // else no service call needed
        me._overlaySpinnerService.HideLayoutSpinner();
      });
    }
  }

  /**When small view is true, navigation back by swiping from left to right.*/
  public SwiperNavigateBack() {
    const me = this;
    if (me.isSmallView == true) {
      me._location.back();
      // todo sollte es nicht zurück auf die landing page?
    }
  }
}
